import { useCallback, useMemo } from "react";
import { useTenant } from "hooks/settings";
import { backendApi } from "src/store/api/enhancedApi";
import { useAppDispatch } from "src/store/store";
import {
  quantisedWindow,
  useLoadedWindow,
  type Chunk,
  type ChunkData,
} from "components/scrapTracking/helpers";
import type {
  TrainableAngleConfig,
  TrainableWindow,
} from "src/store/api/generatedApi";

const CHUNK_SIZE_MS = 7 * 24 * 60 * 60 * 1000;
const CHUNK_BUFFER = 0;

type UseTrainablePeriodsDataResult = Readonly<{
  windows: readonly TrainableWindow[];
  chunks: ChunkData<readonly TrainableWindow[]>[];
  isLoading: boolean;
}>;

export const useTrainablePeriodsData = (
  start: Date,
  end: Date,
  targetLocation: string,
  angles: readonly TrainableAngleConfig[],
  periodDurationSeconds: number
): UseTrainablePeriodsDataResult => {
  const tenant = useTenant();
  const dispatch = useAppDispatch();

  const enabledAngles = useMemo(
    () => angles.filter((a) => a.enabled),
    [angles]
  );

  const anglesJson = useMemo(
    () => JSON.stringify(enabledAngles),
    [enabledAngles]
  );

  const fetchChunk = useCallback(
    async (chunk: Chunk): Promise<readonly TrainableWindow[]> => {
      if (enabledAngles.length === 0 || !targetLocation) {
        return [];
      }

      const subscription = dispatch(
        backendApi.endpoints.getTrainablePeriods.initiate({
          tenantName: tenant,
          start: new Date(chunk.start).toISOString(),
          end: new Date(chunk.end).toISOString(),
          targetLocation,
          periodDurationSeconds,
          anglesJson,
        })
      );

      try {
        const result = await subscription.unwrap();
        return result.windows;
      } finally {
        subscription.unsubscribe();
      }
    },
    [
      dispatch,
      tenant,
      targetLocation,
      periodDurationSeconds,
      anglesJson,
      enabledAngles.length,
    ]
  );

  const quantised = useMemo(
    () =>
      quantisedWindow(
        { start: start.getTime(), end: end.getTime() },
        CHUNK_SIZE_MS,
        CHUNK_BUFFER
      ),
    [start, end]
  );

  const { chunks } = useLoadedWindow(quantised, fetchChunk);

  const mergedWindows = useMemo(() => {
    const allWindows: TrainableWindow[] = [];
    for (const chunk of chunks) {
      if (chunk.value.status === "success") {
        allWindows.push(...chunk.value.data);
      }
    }
    return allWindows.sort(
      (a, b) =>
        new Date(a.window_start).getTime() - new Date(b.window_start).getTime()
    );
  }, [chunks]);

  const isLoading = chunks.some((chunk) => chunk.value.status === "loading");

  return { windows: mergedWindows, chunks, isLoading };
};
